import { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";
import {
  getService,
  putS3File,
  cancelRequest,
} from "../../../services/axios/services";
import { getThesaurus } from "../../../services/api/thesaurus/thesaurus.api";
import { toastError } from "../../../utils";
import { validateAssessmentFile } from "./AssessmentsUploadModal.utils";

const ASSESSMENT_TYPE_THESAURUS_ORIGIN = 39;

const createFileEntry = (file) => ({
  id: `${file.name}-${file.size}-${file.lastModified}`,
  originalName: file.name,
  _meta: file,
  isSending: false,
  isSent: false,
  isSaved: false,
});

// Manages the assessment files list and uploads each one to S3 via the same presigned-url flow used by attachments
const useAssessmentsUpload = () => {
  const [files, setFiles] = useState([]);
  const [assessmentTypeId, setAssessmentTypeId] = useState(null);
  const isMountedRef = useRef(true);
  const filesRef = useRef(files);
  const presignSourceRef = useRef(null);

  filesRef.current = files;

  // aborts presigned-url requests and in-flight S3 uploads
  const cancelPending = useCallback(() => {
    if (presignSourceRef.current) {
      presignSourceRef.current.cancel();
      presignSourceRef.current = null;
    }

    filesRef.current.forEach((entry) => {
      if (entry.isSending && !entry.isSent) cancelRequest(entry);
    });
  }, []);

  useEffect(
    () => () => {
      isMountedRef.current = false;
      cancelPending();
    },
    [cancelPending]
  );

  // the assessment type is fixed (not user-selectable), always the first thesaurus item for origin 39
  useEffect(() => {
    const source = axios.CancelToken.source();

    getThesaurus({
      origins: ASSESSMENT_TYPE_THESAURUS_ORIGIN,
      cancelToken: source.token,
    })
      .then((res) => {
        if (isMountedRef.current) {
          setAssessmentTypeId(res.items?.[0]?.id ?? null);
        }
      })
      .catch((err) => {
        if (axios.isCancel(err) || !isMountedRef.current) return;
        setAssessmentTypeId(null);
        toastError(
          "Não foi possível carregar a informação do tipo de parecer no thesaurus"
        );
      });

    return () => source.cancel();
  }, []);

  const uploadFile = (entry) => {
    setFiles((current) =>
      current.map((item) =>
        item.id === entry.id ? { ...item, isSending: true } : item
      )
    );

    if (!presignSourceRef.current) {
      presignSourceRef.current = axios.CancelToken.source();
    }

    getService({
      endpoint: "contents/attachments/presignedurl/upload",
      params: {
        fileName: entry.originalName,
        contentType: entry._meta.type || "application/octet-stream",
      },
      options: { cancelToken: presignSourceRef.current.token },
    })
      .then((res) =>
        putS3File({ url: res.data.url, file: entry, progress: () => {} }).then(
          () => res.data.fileId
        )
      )
      .then((fileId) => {
        if (!isMountedRef.current) return;
        setFiles((current) =>
          current.map((item) =>
            item.id === entry.id
              ? { ...item, isSending: false, isSent: true, fileId }
              : item
          )
        );
      })
      .catch((err) => {
        if (!isMountedRef.current) return;
        if (!axios.isCancel(err)) {
          toastError("Não foi possível enviar o arquivo", entry.originalName);
        }
        setFiles((current) => current.filter((item) => item.id !== entry.id));
      });
  };

  const addFiles = (fileList) => {
    const rejected = [];
    const accepted = [];

    fileList.forEach((file) => {
      const validation = validateAssessmentFile(file);
      const isDuplicate = files.some(
        (item) =>
          item.originalName === file.name && item._meta.size === file.size
      );

      if (!validation.valid) {
        rejected.push({ file, reason: validation.reason });
      } else if (!isDuplicate) {
        accepted.push(createFileEntry(file));
      }
    });

    if (accepted.length > 0) {
      setFiles((current) => [...current, ...accepted]);
      accepted.forEach(uploadFile);
    }

    return { rejected };
  };

  const removeFile = (id) => {
    const entry = files.find((item) => item.id === id);

    if (entry && entry.isSending && !entry.isSent) {
      cancelRequest(entry);
    }

    setFiles((current) => current.filter((item) => item.id !== id));
  };

  const reset = () => setFiles([]);

  // marks assessments that were already created so a retry after a partial failure doesn't recreate them
  const markFilesAsSaved = (ids) => {
    setFiles((current) =>
      current.map((item) =>
        ids.includes(item.id) ? { ...item, isSaved: true } : item
      )
    );
  };

  const isUploading = files.some((item) => item.isSending && !item.isSent);
  const hasCompletedFile = files.some((item) => item.isSent && !item.isSaved);

  return {
    files,
    addFiles,
    removeFile,
    reset,
    markFilesAsSaved,
    cancelPending,
    isUploading,
    hasCompletedFile,
    assessmentTypeId,
  };
};

export default useAssessmentsUpload;
